import { useQuery } from '@tanstack/react-query';
import { api } from '../../../../singleton/axios';
import dayjs from 'dayjs';

export interface VolumeRecord {
  date: string;
  open: number;
  low: number;
  high: number;
  close: number;
  volume: number;
}

export interface DailyVolumeRecord {
  date: string;
  volume: number;
}

export interface VolumeDataResponse {
  data: VolumeRecord[];
  metadata: {
    requestedDate: string;
    actualDate: string;
    fallbackUsed: boolean;
    recordCount: number;
  };
}

export interface VolumeDataWithMetadata {
  data: VolumeRecord[];
  requestedDate: string;
  actualDate: string;
  fallbackUsed: boolean;
  recordCount: number;
}

export type VolumeTimeframe = '15min' | 'monthly';
export type VolumeResult = VolumeDataWithMetadata | DailyVolumeRecord[];

const getMostRecentTradingDay = (): string => {
  const today = dayjs();
  const dayOfWeek = today.day(); // 0 = Sunday, 6 = Saturday
  
  // If it's Saturday (6), go back to Friday
  if (dayOfWeek === 6) {
    return today.subtract(1, 'day').format('YYYY-MM-DD');
  }
  
  // If it's Sunday (0), go back to Friday
  if (dayOfWeek === 0) {
    return today.subtract(2, 'days').format('YYYY-MM-DD');
  }
  
  // Otherwise, use today (Monday-Friday)
  return today.format('YYYY-MM-DD');
};

export const useCompanyVolume = (
  ticker: string | undefined, 
  timeframe: VolumeTimeframe = '15min',
  tradingDate?: string
) => {
  const effectiveTradingDate = tradingDate || getMostRecentTradingDay();
  
  return useQuery<VolumeResult, Error>({
    queryKey: ['companyVolume', ticker, timeframe, effectiveTradingDate],
    queryFn: async () => {
      if (!ticker) {
        throw new Error('Ticker is required');
      }

      if (timeframe === 'monthly') {
        // For monthly view, get past 30 days of daily volume data (old format)
        const toDate = dayjs().format('YYYY-MM-DD');
        const fromDate = dayjs().subtract(30, 'days').format('YYYY-MM-DD');
        
        const params = { 
          ticker,
          from: fromDate,
          to: toDate,
        };
        
        const { data } = await api.get<DailyVolumeRecord[]>('/company-volume-monthly', {
          params,
        });
        return data;
      } else {
        // For 15min view, get single trading day data (new format with metadata)
        const params: any = { 
          ticker,
          from: effectiveTradingDate,
          to: effectiveTradingDate,
        };
        
        const { data: response } = await api.get<VolumeDataResponse>('/company-volume', {
          params,
        });
        
        // Transform the response to a flatter structure for easier consumption
        return {
          data: response.data,
          requestedDate: response.metadata.requestedDate,
          actualDate: response.metadata.actualDate,
          fallbackUsed: response.metadata.fallbackUsed,
          recordCount: response.metadata.recordCount,
        };
      }
    },
    enabled: !!ticker,
    refetchOnWindowFocus: false,
    staleTime: timeframe === '15min' ? 15 * 60 * 1000 : 60 * 60 * 1000, // 15 min for intraday, 1 hour for daily
  });
};